"use client";

import { useState } from "react";
import axios from "axios";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import FormContainer from "../../Components/FormContainer";
import DatePickerComponent from "../../Components/DatePickerComponent";
import SecondaryButton from "../../Components/SecondaryButton";
import { toast } from "sonner";

export default function CreateQuote() {
  const [vehicles, setVehicles] = useState([
    { year: "", make: "", model: "", type: "", isOperable: "1" },
  ]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    from: "",
    to: "",
    transportType: "",
    estimatedDate: null,
    name: "",
    phone: "",
    email: "",
  });
  const [errorDetails, setErrorDetails] = useState({});

  const addVehicle = () => {
    setVehicles((v) => [...v, { year: "", make: "", model: "", type: "", isOperable: "1" }]);
  };

  const removeVehicle = (index) => {
    setVehicles((v) => v.filter((_, i) => i !== index));
  };

  const updateVehicle = (index, field, value) => {
    setVehicles((v) =>
      v.map((veh, i) => (i === index ? { ...veh, [field]: value } : veh))
    );
  };

  const handleInputChange = (field, value) => {
    setFormData((d) => ({ ...d, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrorDetails({});

      // validações básicas
      if (
        !formData.from ||
        !formData.to ||
        !formData.transportType ||
        !formData.name ||
        !formData.phone ||
        !formData.email
      ) {
        toast.error("Please fill in all required fields");
        return;
      }
      if (vehicles.some((v) => !v.year || !v.make || !v.model || !v.type)) {
        toast.error("Please complete all vehicle information");
        return;
      }

      // formata data
      const pickupDate = formData.estimatedDate
        ? formData.estimatedDate.toISOString().split("T")[0]
        : null;

      // payload conforme spec
      const quotePayload = {
        pickup_zipcode: formData.from,
        pickup_city: "",
        pickup_state: "",
        delivery_zipcode: formData.to,
        delivery_city: "",
        delivery_state: "",
        transport_type:
          formData.transportType.charAt(0).toUpperCase() +
          formData.transportType.slice(1),
        pickup_date: pickupDate,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        source: "evo_evolution",
        quote_vehicles: vehicles.map((v) => ({
          year: v.year,
          make: v.make,
          model: v.model,
          operable: v.isOperable,
          type: v.type,
        })),
      };

      const response = await axios.post(
        "https://new.cajuu.app/api/quote",
        quotePayload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 201) {
        toast.success("Quote created successfully!");
        setVehicles([{ year: "", make: "", model: "", type: "", isOperable: "1" }]);
        setFormData({
          from: "",
          to: "",
          transportType: "",
          estimatedDate: null,
          name: "",
          phone: "",
          email: "",
        });
      } else {
        toast.error("Failed to create quote. Please try again.");
      }
    } catch (err) {
      console.error("Error creating quote:", err);
      const details = err.response?.data || {};
      setErrorDetails(details);
      toast.error(details.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white p-6">
        <InfoSection title="QUOTE" description="Create Quote" />

        {/* exibe cada mensagem de validação sem tentar renderizar o objeto inteiro */}
        {Object.keys(errorDetails).length > 0 && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-4">
            {Object.entries(errorDetails).map(([field, msgs]) => (
              <p key={field}>
                <strong>{field.replace(/_/g, " ")}:</strong>{" "}
                {Array.isArray(msgs) ? msgs.join(", ") : String(msgs)}
              </p>
            ))}
          </div>
        )}

        <GroupedContainer>
          <InfoSection title="Travel Info" />
          <FormContainer>
            <TextField
              label="From (Zipcode)"
              value={formData.from}
              onChange={(e) => handleInputChange("from", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="To (Zipcode)"
              value={formData.to}
              onChange={(e) => handleInputChange("to", e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Transport Type</InputLabel>
              <Select
                value={formData.transportType}
                onChange={(e) => handleInputChange("transportType", e.target.value)}
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Enclosed">Enclosed</MenuItem>
              </Select>
            </FormControl>
          </FormContainer>
        </GroupedContainer>

        <GroupedContainer>
          <InfoSection title="Vehicles" />
          {vehicles.map((veh, i) => (
            <div key={i} className="flex space-x-2 mb-4">
              <TextField
                label="Year"
                value={veh.year}
                onChange={(e) => updateVehicle(i, "year", e.target.value)}
                required
              />
              <TextField
                label="Make"
                value={veh.make}
                onChange={(e) => updateVehicle(i, "make", e.target.value)}
                required
              />
              <TextField
                label="Model"
                value={veh.model}
                onChange={(e) => updateVehicle(i, "model", e.target.value)}
                required
              />
              <TextField
                label="Vehicle Type"
                value={veh.type}
                onChange={(e) => updateVehicle(i, "type", e.target.value)}
                required
              />
              <FormControl>
                <InputLabel>Operable</InputLabel>
                <Select
                  value={veh.isOperable}
                  onChange={(e) => updateVehicle(i, "isOperable", e.target.value)}
                >
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Select>
              </FormControl>
              {i > 0 && (
                <IconButton color="error" onClick={() => removeVehicle(i)}>
                  <Trash2 />
                </IconButton>
              )}
            </div>
          ))}
          <SecondaryButton onClick={addVehicle}>Add Vehicle</SecondaryButton>
        </GroupedContainer>

        <GroupedContainer>
          <InfoSection title="Date & Contact" />
          <FormContainer>
            <DatePickerComponent
              value={formData.estimatedDate}
              onChange={(d) => handleInputChange("estimatedDate", d)}
              fullWidth
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="E-mail"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
              fullWidth
            />
          </FormContainer>
        </GroupedContainer>

        <div className="flex justify-end mt-4">
          <SecondaryButton onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Submit"}
          </SecondaryButton>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
